/**
 * BookingForm - Step 1: Address, Date, Time, Passengers selection
 *
 * i18n (Phase 5):
 *   - Uses booking.* namespace exclusively (departure, departureAddress,
 *     destination, destinationAddress, date, selectDate, time, passengers,
 *     passenger, passengers_plural, continue, estimatedPrice, fixedFare,
 *     highDemand, highDemandArea, driversReady).
 *   - Receives `t` as a prop from the BookingWidget orchestrator.
 *   - The EUR symbol and HH:MM time slots are intentionally hardcoded
 *     (format primitives). (KG-3R: the fabricated 4.9/5 rating was removed;
 *     Wave 2: the orphaned "120+ satisfiedClients" claim key was removed.)
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, Users, MapPin, ArrowRight, Car, Zap, Loader2, Leaf, Crown, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import AddressAutocomplete from '../AddressAutocomplete';
import { format, startOfDay } from 'date-fns';

const generateTimeSlots = () => {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      slots.push(`${hour}:${minute}`);
    }
  }
  return slots;
};

const timeSlots = generateTimeSlots();

// Map service_type -> icon + accent palette (mirrors BookingSummary's full
// Step 2 cards but in a compact 1-row layout suitable for inline rendering).
const tierStyles = {
  eco:      { Icon: Leaf,  text: 'text-green-600',   ring: 'ring-green-500',   chipBg: 'bg-green-100' },
  van:      { Icon: Users, text: 'text-blue-600',    ring: 'ring-blue-500',    chipBg: 'bg-blue-100' },
  business: { Icon: Car,   text: 'text-neutral-900', ring: 'ring-neutral-900', chipBg: 'bg-neutral-100' },
  // G3.E: PRIORITY removed from customer-widget tier vocabulary.
};

export const BookingForm = ({
  formData,
  setFormData,
  liveEstimate,
  rideOptions,
  selectedOption,
  onSelectOption,
  onContinue,
  isRTL,
  currentLocale,
  t
}) => {
  const handlePickupSelect = (place) => {
    setFormData(prev => ({
      ...prev,
      pickup_address: place.address,
      pickup_lat: place.lat,
      pickup_lng: place.lng
    }));
  };

  const handleDestinationSelect = (place) => {
    setFormData(prev => ({
      ...prev,
      destination_address: place.address,
      destination_lat: place.lat,
      destination_lng: place.lng
    }));
  };

  const canProceed = formData.pickup_address && formData.destination_address && formData.date && formData.time;

  return (
    <div className="space-y-4">
      {/* Pickup Address */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-black font-semibold">
          {t('booking.departure')}
        </Label>
        <div className="relative">
          <MapPin className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400`} />
          <AddressAutocomplete
            value={formData.pickup_address}
            onChange={(val) => setFormData(prev => ({ ...prev, pickup_address: val }))}
            onPlaceSelect={handlePickupSelect}
            placeholder={t('booking.departureAddress')}
            className={isRTL ? 'pr-10' : 'pl-10'}
            id="pickup-address"
          />
        </div>
      </div>

      {/* Destination Address */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-black font-semibold">
          {t('booking.destination')}
        </Label>
        <div className="relative">
          <MapPin className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400`} />
          <AddressAutocomplete
            value={formData.destination_address}
            onChange={(val) => setFormData(prev => ({ ...prev, destination_address: val }))}
            onPlaceSelect={handleDestinationSelect}
            placeholder={t('booking.destinationAddress')}
            className={isRTL ? 'pr-10' : 'pl-10'}
            id="destination-address"
          />
        </div>
      </div>

      {/* Live Estimate Display */}
      <AnimatePresence>
        {liveEstimate && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-neutral-50 border border-neutral-200 rounded-xl p-4"
          >
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full animate-pulse ${liveEstimate.surge_active ? 'bg-orange-500' : 'bg-green-500'}`} />
                <span className="text-sm text-black font-semibold">{t('booking.estimatedPrice')}</span>
              </div>
              <span className="text-2xl font-heading font-bold text-black" data-testid="live-price-estimate">
                ~ {liveEstimate.estimated_price} €
              </span>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-neutral-400">
              <span>{liveEstimate.distance_km} km</span>
              <span>~{liveEstimate.duration_minutes} min</span>
              {liveEstimate.is_fixed_fare && (
                <span className="px-2 py-0.5 bg-green-100 text-green-600 rounded-full">{t('booking.fixedFare')}</span>
              )}
            </div>
            {liveEstimate.surge_active && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg"
                data-testid="surge-indicator"
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-orange-500" />
                  <span className="text-orange-600 text-sm font-medium">
                    {t('booking.highDemand')} · x{liveEstimate.surge_multiplier}
                  </span>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inline ride options — Step 1 lets the user pick a tier before
          continuing, so they see all prices upfront and Step 2 opens already
          configured. Layout pairs ECO+PRIORITY and VAN+BUSINESS. Visual style
          mirrors the Date/Time pickers (white bg, neutral-200 border) so the
          tier picker reads as part of the same form, not a louder secondary
          panel — the tier's brand colour is preserved on the icon and on the
          selection ring. */}
      <AnimatePresence>
        {rideOptions?.options?.length > 0 && (() => {
          const order = ['eco', 'business', 'van'];  // G3.E: PRIORITY removed
          const optionsByType = Object.fromEntries(rideOptions.options.map(o => [o.service_type, o]));
          const orderedOptions = order.map(t => optionsByType[t]).filter(Boolean);
          if (orderedOptions.length === 0) return null;
          return (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-2"
              data-testid="inline-ride-options"
            >
              <Label className="text-xs uppercase tracking-[0.2em] text-black font-semibold">
                {t('booking.chooseRide')}
              </Label>
              <div className="grid grid-cols-2 gap-3">
                {orderedOptions.map((option) => {
                  const style = tierStyles[option.service_type] || tierStyles.business;
                  const Icon = style.Icon;
                  const isSelected = selectedOption?.service_type === option.service_type;
                  return (
                    <button
                      type="button"
                      key={option.service_type}
                      onClick={() => onSelectOption?.(option)}
                      className={`text-left p-3 rounded-xl border bg-white transition-colors ${
                        isSelected
                          ? `border-transparent ring-2 ${style.ring} shadow-sm`
                          : 'border-neutral-200 hover:bg-neutral-50 hover:border-neutral-300'
                      }`}
                      data-testid={`inline-ride-option-${option.service_type}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${style.chipBg}`}>
                          <Icon className={`w-4 h-4 ${style.text}`} />
                        </div>
                        <span className={`font-heading font-semibold text-sm ${style.text} truncate flex-1`}>
                          {option.service_name}
                        </span>
                        {isSelected && <Check className={`w-4 h-4 flex-shrink-0 ${style.text}`} />}
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-xs text-neutral-500 truncate">
                          {option.estimated_pickup_minutes != null ? `~${option.estimated_pickup_minutes} min` : ''}
                        </span>
                        <span className="text-base font-bold text-black whitespace-nowrap">
                          {option.estimated_price}€
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Date and Time */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-black font-semibold">{t('booking.date')}</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left bg-white border-neutral-200 text-neutral-900 hover:bg-neutral-50 hover:border-neutral-300 rounded-xl h-11"
                data-testid="date-picker-trigger"
              >
                <Calendar className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 text-neutral-400`} />
                {formData.date ? format(formData.date, 'dd/MM/yyyy', { locale: currentLocale }) : t('booking.selectDate')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white border-neutral-200 rounded-xl shadow-xl" align="start">
              <CalendarComponent
                mode="single"
                selected={formData.date}
                onSelect={(date) => setFormData(prev => ({ ...prev, date }))}
                disabled={(date) => date < startOfDay(new Date())}
                locale={currentLocale}
                className="bg-white text-neutral-900 rounded-xl"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-black font-semibold">{t('booking.time')}</Label>
          <Select 
            value={formData.time} 
            onValueChange={(val) => setFormData(prev => ({ ...prev, time: val }))}
          >
            <SelectTrigger 
              className="bg-white border-neutral-200 text-neutral-900 hover:border-neutral-300 rounded-xl h-11"
              data-testid="time-select-trigger"
            >
              <Clock className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 text-neutral-400`} />
              <SelectValue placeholder={t('booking.time')} />
            </SelectTrigger>
            <SelectContent className="bg-white border-neutral-200 rounded-xl max-h-60 shadow-xl">
              {timeSlots.map(slot => (
                <SelectItem key={slot} value={slot} className="text-neutral-900 hover:bg-neutral-100 rounded-lg">
                  {slot}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Passengers */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-black font-semibold">{t('booking.passengers')}</Label>
        <Select 
          value={formData.passengers} 
          onValueChange={(val) => setFormData(prev => ({ ...prev, passengers: val }))}
        >
          <SelectTrigger 
            className="bg-white border-neutral-200 text-neutral-900 hover:border-neutral-300 rounded-xl h-11"
            data-testid="passengers-select"
          >
            <Users className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 text-neutral-400`} />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-neutral-200 rounded-xl shadow-xl">
            {[1, 2, 3, 4, 5, 6, 7].map(n => (
              <SelectItem key={n} value={n.toString()} className="text-neutral-900 hover:bg-neutral-100 rounded-lg">
                {n} {n > 1 ? t('booking.passengers_plural') : t('booking.passenger')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Driver Availability - Dynamic based on demand */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center justify-center gap-2 py-2 text-sm"
      >
        <Car className="w-4 h-4 text-green-500" />
        <span className="text-green-600 font-medium" data-testid="urgency-signal">
          {liveEstimate?.surge_active 
            ? t('booking.highDemandArea')
            : t('booking.driversReady')}
        </span>
      </motion.div>

      {/* Continue Button */}
      <Button
        type="button"
        onClick={onContinue}
        disabled={!canProceed}
        className="w-full bg-black text-white hover:bg-neutral-800 py-6 text-base font-semibold rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
        data-testid="continue-to-step-2"
      >
        {t('booking.continue')}
        <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
      </Button>

      {/* KG-3R (Truth Before Reach): fabricated 4.9/5 star rating removed — no real reviews. */}
    </div>
  );
};

export default BookingForm;
